import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { DashboardLayout } from "component/layout/dashboard";
import { FeatherIcon } from "component/shared/feather-icon";
import { Switch } from "component/shared/switch";
import { SettingsSection } from "component/shared/settings-page";
import { FormSelectField } from "component/shared/form-fields";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import {
	RecipientMultiSelect,
	RecipientOption,
	AddRecipientsDialog,
	DeleteRecipientDialog,
} from "component/shared/recipient-multi-select";
import { PageProps } from "shared/page-props";
import * as paths from "routes/paths";
import { User } from "model/user";
import { RootState } from "redux/store";
import { saveUser } from "redux/slice/authentication";
import { fetchEmailRoutingRules, saveEmailRoutingRules } from "redux/slice/email-routing-rules";
import {
	fetchEmailRecipients,
	addEmailRecipients,
	deleteEmailRecipient,
} from "redux/slice/email-recipients";
import { getEmailRoutingRules, getEmailRecipients } from "redux/selector";
import { EmailRoutingRuleResource } from "api/resource/email-routing-rule";
import { displayOrder, leadActivityLabels } from "./lead-activity-labels";
import { RefreshTable } from "component/shared/refresh-table";
import { RestoreDefaultsDialog } from "./restore-defaults-dialog";
import { styles } from "./refreshed-style";

const ACCOUNT_OWNER = "accountOwner";
const ASSIGNED_AGENT = "assignedAgent";

const FREQUENCIES = [
	{ value: "daily", label: "Daily" },
	{ value: "weekly", label: "Weekly" },
	{ value: "monthly", label: "Monthly" },
];

const mapStateToProps = (state: RootState) => ({
	authLoading: state.authentication.loading,
	authError: state.authentication.error,
	rules: getEmailRoutingRules(state),
	recipients: getEmailRecipients(state),
	rulesSaving: state.emailRoutingRules.loading,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			saveUser,
			fetchEmailRoutingRules,
			saveEmailRoutingRules,
			fetchEmailRecipients,
			addEmailRecipients,
			deleteEmailRecipient,
		},
		dispatch,
	);

interface Props
	extends PageProps,
		Router.RouteComponentProps,
		WithStyles<typeof styles>,
		ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps> {
	user: User;
}

/** Per-row recipient selection, keyed by emailMasterId. */
interface RowSelection {
	[emailMasterId: number]: string[];
}

interface State {
	receiveTextMessages: boolean;
	summaryEnabled: boolean;
	receiveLeadActivity: string;
	selection: RowSelection;
	addOpen: boolean;
	restoreOpen: boolean;
	deleteId: number | null;
	saving: boolean;
	snackbarOpen: boolean;
}

/**
 * The frequency dropdown and the "never" case are the same stored field: receiveLeadActivity
 * holds "none" when the summary is off. The design splits that into a toggle plus a frequency,
 * so the toggle is derived rather than stored separately.
 */
function summaryStateFromUser(user: User) {
	const frequency = user.receiveLeadActivity || "daily";
	return {
		summaryEnabled: frequency !== "none",
		receiveLeadActivity: frequency === "none" ? "daily" : frequency,
	};
}

function selectionFromRules(rules: EmailRoutingRuleResource[]): RowSelection {
	const selection: RowSelection = {};
	for (const rule of rules) {
		const ids: string[] = [];
		if (rule.mainContactYn) {
			ids.push(ACCOUNT_OWNER);
		}
		if (rule.agentYn) {
			ids.push(ASSIGNED_AGENT);
		}
		for (const recipientId of rule.recipientIds ?? []) {
			ids.push(String(recipientId));
		}
		selection[rule.emailMasterId] = ids;
	}
	return selection;
}

class Component extends React.Component<Props, State> {

	public constructor(props: Props) {
		super(props);
		this.state = {
			receiveTextMessages: !!props.user.receiveTextMessages,
			...summaryStateFromUser(props.user),
			selection: selectionFromRules(props.rules),
			addOpen: false,
			restoreOpen: false,
			deleteId: null,
			saving: false,
			snackbarOpen: false,
		};
	}

	public componentDidMount() {
		this.props.fetchEmailRoutingRules();
		this.props.fetchEmailRecipients();
	}

	public componentDidUpdate(prevProps: Props) {
		// Seed the selection once the rules arrive, and again if they are refetched.
		if (prevProps.rules !== this.props.rules) {
			this.setState({ selection: selectionFromRules(this.props.rules) });
		}
		const { authLoading, authError } = this.props;
		if (prevProps.authLoading && !authLoading && !authError && this.state.saving) {
			this.setState({ saving: false });
		}
	}

	/**
	 * The eight rows in the design, in the design's order.
	 *
	 * An allowlist, not a denylist. EmailMaster is a catalog of every transactional email the
	 * platform sends -- QuickSite publish notices, plugin setup invitations, cancellation emails
	 * -- and the endpoint returns a routing rule for all of them. Filtering only the three
	 * explicitly hidden pages left roughly twenty unrelated rows on the page, including
	 * "Disclosure Reports Request Summary" and "Existing Lead Adds A Property Organizer Account",
	 * which this ticket exists to remove.
	 *
	 * Keyed on displayOrder so adding a row is a one-line change there, and so a catalog addition
	 * never appears on this page unaccompanied by a label and a product decision.
	 */
	private visibleRules(): EmailRoutingRuleResource[] {
		const byPage = new Map<string, EmailRoutingRuleResource>();
		for (const rule of this.props.rules) {
			if (!byPage.has(rule.designPageName)) {
				byPage.set(rule.designPageName, rule);
			}
		}
		return displayOrder
			.map((designPageName) => byPage.get(designPageName))
			.filter((rule): rule is EmailRoutingRuleResource => !!rule);
	}

	private recipientOptions(rule: EmailRoutingRuleResource): RecipientOption[] {
		const { user, recipients } = this.props;
		const options: RecipientOption[] = [];
		if (rule.useMainContactYn) {
			options.push({
				id: ACCOUNT_OWNER,
				label: "Account Owner",
				// MAX-3952 added notificationEmail; it falls back to the account email.
				detail: user.notificationEmail || user.companyEmail,
			});
		}
		if (rule.useAgentYn) {
			options.push({ id: ASSIGNED_AGENT, label: "Assigned Agent" });
		}
		for (const recipient of recipients) {
			options.push({
				id: String(recipient.clientEmailRecipientId),
				label: recipient.email,
			});
		}
		return options;
	}

	private isDirty(): boolean {
		const { user, rules } = this.props;
		const originalSummary = summaryStateFromUser(user);
		if (
			this.state.receiveTextMessages !== !!user.receiveTextMessages ||
			this.state.summaryEnabled !== originalSummary.summaryEnabled ||
			this.state.receiveLeadActivity !== originalSummary.receiveLeadActivity
		) {
			return true;
		}
		const original = selectionFromRules(rules);
		return Object.keys(this.state.selection).some((key) => {
			const id = Number(key);
			const before = [...(original[id] ?? [])].sort().join(",");
			const after = [...this.state.selection[id]].sort().join(",");
			return before !== after;
		});
	}

	private handleSave = () => {
		const { user, saveUser, saveEmailRoutingRules, rules } = this.props;
		const { receiveTextMessages, summaryEnabled, receiveLeadActivity, selection } = this.state;

		// Two save paths: the toggles live on the user object, the table on the routing rules
		// endpoint. The sticky footer implies one action, so both fire together.
		saveUser({
			user: {
				...user,
				receiveTextMessages,
				receiveLeadActivity: summaryEnabled ? receiveLeadActivity : "none",
			},
		});

		saveEmailRoutingRules(
			rules.map((rule) => {
				const ids = selection[rule.emailMasterId] ?? [];
				const custom = ids.filter((id) => id !== ACCOUNT_OWNER && id !== ASSIGNED_AGENT);
				return {
					emailMasterId: rule.emailMasterId,
					mainContactYn: ids.includes(ACCOUNT_OWNER),
					agentYn: ids.includes(ASSIGNED_AGENT),
					listingAgentYn: !!rule.listingAgentYn,
					// Left as-is: the legacy page owns these, and this page writes recipients
					// through recipientIds instead.
					otherYn: !!rule.otherYn,
					otherEmail: rule.otherEmail,
					recipientIds: custom.map((id) => Number(id)),
				};
			}),
		);

		this.setState({ saving: true, snackbarOpen: true });
	};

	private handleCancel = () => {
		const { user, rules } = this.props;
		this.setState({
			receiveTextMessages: !!user.receiveTextMessages,
			...summaryStateFromUser(user),
			selection: selectionFromRules(rules),
		});
	};

	/**
	 * Restore Defaults selects Account Owner and Assigned Agent on every row and clears custom
	 * recipients from the rows. Per product, the addresses stay available in the dropdowns — only
	 * the assignments go, which is why this touches selection and not the recipient list.
	 */
	private handleRestoreDefaults = () => {
		this.setState({ restoreOpen: false });
		const selection: RowSelection = {};
		for (const rule of this.props.rules) {
			const ids: string[] = [];
			if (rule.useMainContactYn) {
				ids.push(ACCOUNT_OWNER);
			}
			if (rule.useAgentYn) {
				ids.push(ASSIGNED_AGENT);
			}
			selection[rule.emailMasterId] = ids;
		}
		this.setState({ selection });
	};

	private handleAddRecipients = async (emails: string[]) => {
		this.setState({ addOpen: false });
		await this.props.addEmailRecipients(emails.map((email) => ({ email })));
	};

	private handleDeleteRecipient = async () => {
		const { deleteId } = this.state;
		if (deleteId == null) {
			return;
		}
		this.setState({ deleteId: null });
		await this.props.deleteEmailRecipient({ clientEmailRecipientId: deleteId });
		// The address is gone from the list, so drop it from every row's selection too.
		const removed = String(deleteId);
		this.setState((prev) => {
			const selection: RowSelection = {};
			for (const key of Object.keys(prev.selection)) {
				const id = Number(key);
				selection[id] = prev.selection[id].filter((value) => value !== removed);
			}
			return { selection };
		});
	};

	private renderTextAndSummary() {
		const { classes = {} } = this.props;
		const { receiveTextMessages, summaryEnabled, receiveLeadActivity } = this.state;
		return (
			<SettingsSection title="Text & Summary Notifications">
				<Mui.Box mb={3}>
					<Switch
						checked={receiveTextMessages}
						onChange={(value) => this.setState({ receiveTextMessages: value })}
						label="Send text notifications for lead activity"
						description="When enabled, text messages will be sent to your account owner's mobile number when a lead submits an information, contact, showing, or valuation request."
					/>
				</Mui.Box>
				<Switch
					checked={summaryEnabled}
					onChange={(value) => this.setState({ summaryEnabled: value })}
					label="Send lead activity summary"
					description="When enabled, a periodic summary of lead activity will be sent via email to your account owner's email, or your lead notification email if one is set."
				/>
				{summaryEnabled && (
					<Mui.Box className={classes.frequency}>
						<FormSelectField
							label="Frequency"
							value={receiveLeadActivity}
							onChange={(value) => this.setState({ receiveLeadActivity: value as string })}
							options={FREQUENCIES}
							valueExtractor={(frequency) => frequency.value}
							labelExtractor={(frequency) => frequency.label}
						/>
					</Mui.Box>
				)}
			</SettingsSection>
		);
	}

	private renderTable() {
		const { classes = {}, recipients } = this.props;
		const rules = this.visibleRules();
		return (
			<>
				<Mui.Typography className={`${classes.sectionHeading} ${classes.emailsTitle}`}>
					Lead Activity Emails
				</Mui.Typography>
				<Mui.Typography className={`${classes.sectionDescription} ${classes.emailsDescription}`}>
					Choose who's emailed for each type of lead activity, or add additional recipients.
					Account Owner defaults to your account's email, or a lead notification email if one is
					set.
				</Mui.Typography>
				<Mui.Box className={classes.restoreRow}>
					<Mui.Button
						variant="text"
						className={classes.restoreDefaults}
						onClick={() => this.setState({ restoreOpen: true })}
					>
						Restore Defaults
					</Mui.Button>
				</Mui.Box>
				<RefreshTable
					items={rules}
					rowKey={(rule) => rule.emailMasterId}
					sort={{ id: "", desc: false }}
					paginate={false}
					columns={[
						{
							id: "activity",
							title: "Lead Activity",
							value: (rule) => leadActivityLabels[rule.designPageName] ?? rule.shortDesc,
						},
						{
							id: "recipients",
							title: "Email Recipients",
							align: "right",
							width: 576,
							value: (rule) => (
								<RecipientMultiSelect
									options={this.recipientOptions(rule)}
									selectedIds={this.state.selection[rule.emailMasterId] ?? []}
									onChange={(ids) =>
										this.setState((prev) => ({
											selection: { ...prev.selection, [rule.emailMasterId]: ids },
										}))
									}
									onAddRecipient={() => this.setState({ addOpen: true })}
									deletableIds={recipients.map((recipient) =>
										String(recipient.clientEmailRecipientId),
									)}
									onDeleteRecipient={(id) => this.setState({ deleteId: Number(id) })}
								/>
							),
						},
					]}
				/>
			</>
		);
	}

	public render() {
		const { user, authError, authLoading, recipients, classes = {} } = this.props;
		const { addOpen, deleteId, saving, snackbarOpen } = this.state;
		const dirty = this.isDirty();
		const deleting = recipients.find(
			(recipient) => recipient.clientEmailRecipientId === deleteId,
		);
		return (
			<DashboardLayout
				permitted={!!user.admin}
				title="Lead Activity"
				header={
					<Mui.Typography variant="h1">
						<FeatherIcon>
							<Icons.Bell />
						</FeatherIcon>
						Lead Activity
					</Mui.Typography>
				}
			>
				<Mui.Typography variant="body2" className={classes.pageSubtitle}>
					Configure who gets notified when a lead takes action on your website. Defaults have
					been pre-configured to get you started — adjust as needed. To update contact
					information, go to <Router.Link to={paths.settingsAccount}>Account</Router.Link>.
				</Mui.Typography>

				{this.renderTextAndSummary()}
				<Mui.Divider className={classes.sectionDivider} />
				{this.renderTable()}

				<AddRecipientsDialog
					open={addOpen}
					onClose={() => this.setState({ addOpen: false })}
					onAdd={this.handleAddRecipients}
					existingEmails={recipients.map((recipient) => recipient.email)}
				/>

				<RestoreDefaultsDialog
					open={this.state.restoreOpen}
					onClose={() => this.setState({ restoreOpen: false })}
					onConfirm={this.handleRestoreDefaults}
				/>

				<DeleteRecipientDialog
					open={deleteId != null}
					email={deleting ? deleting.email : ""}
					onClose={() => this.setState({ deleteId: null })}
					onConfirm={this.handleDeleteRecipient}
				/>

				<SettingsStickyFooter
					dirty={dirty}
					saving={!!(saving && authLoading)}
					authError={authError ?? null}
					onSave={this.handleSave}
					onCancel={this.handleCancel}
				/>

				<UnsavedChangesPrompt isDirty={() => this.isDirty()} history={this.props.history} />

				<Mui.Snackbar
					open={snackbarOpen && !authLoading && !authError}
					message="Lead activity settings saved"
					autoHideDuration={6000}
					onClose={() => this.setState({ snackbarOpen: false })}
				/>
			</DashboardLayout>
		);
	}

}

export const RefreshedLeadActivity = withStyles(
	connect(mapStateToProps, mapDispatchToProps)(Component),
	styles,
);
